import { PublishedCard } from "../types";
import {
  CreateCardTranslate,
  normalizeNewsContent,
} from "./createCardTransformShared";

/* Shared helpers for mapping template page data into editor-friendly props.
 * These helpers translate backend page content into the shape expected by the
 * page experience components when the template modal is rehydrated.
 */
export interface PageData {
  order: number;
  type: string;
  content: any;
  id?: number;
}

interface MapContentToExperienceDataParams {
  page: PageData;
  card: PublishedCard | null;
  usesEndingImage: boolean;
  t: CreateCardTranslate;
}

const normalizeMaybeUrl = (value: any): string | File => {
  if (value instanceof File) return value;
  if (typeof value === "string") return value;
  if (Array.isArray(value) && typeof value[0] === "string")
    return value[0] || "";
  if (value && typeof value.url === "string") return value.url;
  return "";
};

export const mapContentToExperienceData = ({
  page,
  card,
  usesEndingImage,
  t,
}: MapContentToExperienceDataParams): any => {
  const pageType = page?.type || "";
  const normalizedType = pageType
    ? pageType.replace(/_/g, "").toLowerCase().trim()
    : "";
  const content = page?.content || {};

  if (
    normalizedType === "coverimage" ||
    normalizedType === "cover_image" ||
    normalizedType === "endingimage" ||
    pageType === "CoverImage" ||
    pageType === "EndingImage"
  ) {
    const isEndingImage = normalizedType === "endingimage" || pageType === "EndingImage";
    return {
      imageUrl:
        typeof content.imageUrl === "string"
          ? content.imageUrl
          : isEndingImage
            ? card?.endingImageUrl || ""
            : card?.coverImageUrl || "",
    };
  }

  if (normalizedType === "text" || pageType === "Text") {
    return {
      title: content.title || "",
      description: content.description || "",
    };
  }

  if (normalizedType === "about" || pageType === "About") {
    return {
      title: content.title || "",
      description: content.description || "",
      descriptionImage:
        content.description_image || content.descriptionImage || "",
      extraDescription:
        content.extra_description || content.extraDescription || "",
      extraDescriptionImage:
        content.extra_description_image || content.extraDescriptionImage || "",
      moodNote: content.mood_note || content.moodNote || "",
      momentImage: content.moment_image || content.momentImage || "",
      moments: Array.isArray(content.moments) ? content.moments : [],
      credits: Array.isArray(content.credits) ? content.credits : [],
    };
  }

  if (normalizedType === "bonus" || pageType === "Bonus") {
    return {
      title: content.title || "",
      description: content.description || "",
      voiceNote: content.voice_note || content.voiceNote || "",
      image: content.image || "",
      demoSnippet: content.demo_snippet || content.demoSnippet || "",
      cardLinks: Array.isArray(content.card_links || content.cardLinks)
        ? content.card_links || content.cardLinks
        : [],
    };
  }

  if (normalizedType === "instrument" || pageType === "Instrument") {
    return {
      title: content.title || "",
      url: content.url || "",
      artistName: content.artist_name || content.artistName || "",
      description: content.description || "",
    };
  }

  if (normalizedType === "image" || pageType === "Image") {
    let imagesArray: string[] = [];

    if (Array.isArray(content.images)) {
      imagesArray = content.images.slice(0, 8);
    } else if (Array.isArray(content.url)) {
      imagesArray = content.url
        .slice(0, 8)
        .filter((url: any) => url && url.trim());
    } else if (content.imageUrl) {
      imagesArray = [content.imageUrl];
    } else if (content.url && typeof content.url === "string") {
      imagesArray = [content.url];
    }

    return {
      images: imagesArray,
      description: content.description || "",
    };
  }

  if (normalizedType === "audio" || pageType === "Audio") {
    return {
      audioUrl: content.audioUrl || content.url || "",
      description: content.description || "",
      image_url: content.image_url || "",
      ...(usesEndingImage && {
        songTitle:
          content.songTitle || content.song_title || content.title || "",
        artistName: content.artistName || content.artist_name || "",
        lyrics_text: content.lyrics_text || content.lyricsText || "",
      }),
    };
  }

  if (normalizedType === "video" || pageType === "Video") {
    return {
      videoUrl:
        content.videoUrl || content.url || content.video_url || "",
      description: content.description || "",
    };
  }

  if (
    normalizedType === "imagegrid" ||
    normalizedType === "image_grid" ||
    pageType === "ImageGrid"
  ) {
    let imagesArray: string[] = [];

    if (Array.isArray(content.images)) {
      imagesArray = content.images.slice(0, 8);
    } else if (Array.isArray(content.url)) {
      imagesArray = content.url
        .slice(0, 8)
        .filter((url: any) => url && url.trim());
    } else if (content.imageUrl) {
      imagesArray = [content.imageUrl];
    } else if (content.url && typeof content.url === "string") {
      imagesArray = [content.url];
    }

    return {
      images: imagesArray,
      description: content.description || "",
    };
  }

  if (
    normalizedType === "instantcard" ||
    normalizedType === "instant_card" ||
    pageType === "InstantCard" ||
    pageType === "Instant_Card"
  ) {
    return {
      suit: (content.suit as "" | "Heart" | "Spade") || "Heart",
      mode: content.mode || "",
      description: content.description || "",
      chip: content.chip || "",
      availability: content.availability ?? true,
      duration: (content.duration as "" | "8" | "15") || "8",
    };
  }

  if (normalizedType === "songs" || pageType === "Songs") {
    return {
      songs: Array.isArray(content.songs) ? content.songs : [],
    };
  }

  if (
    normalizedType === "flashcard" ||
    normalizedType === "flash_card" ||
    pageType === "FlashCard" ||
    pageType === "Flash_Card"
  ) {
    return {
      title: typeof content.title === "string" ? content.title : "",
      show_time: content.show_time || "",
      price: content.price ?? "",
      duration: content.duration ?? "",
      minimum_audience: content.minimum_audience ?? "",
      allow_observer: content.allow_observer ?? true,
    };
  }

  if (
    normalizedType === "stagecard" ||
    normalizedType === "stage_card" ||
    pageType === "StageCard" ||
    pageType === "Stage_Card"
  ) {
    return {
      type: content.type || "Normal",
      title: typeof content.title === "string" ? content.title : "",
      short_description: content.short_description || "",
      venue_size:
        (content.venue_size as "Small" | "Medium" | "Large" | "Broadcast") ||
        "Medium",
      category: content.category || "",
      sub_category: content.sub_category || "",
      banner_title: content.banner_title || "",
      price: content.price ?? "",
      duration: content.duration ?? "",
      tags: content.tags || [],
      status: (content.status as "Draft" | "Active" | "Paused") || "Draft",
    };
  }

  if (
    normalizedType === "staicard" ||
    normalizedType === "stai_card" ||
    pageType === "StaiCard" ||
    pageType === "Stai_Card"
  ) {
    return {
      talent_name: content.talent_name || "",
      title: typeof content.title === "string" ? content.title : "",
      description: content.description || "",
      temporary_price: content.temporary_price ?? "",
      permanent_price: content.permanent_price ?? "",
      duration: content.duration ?? "",
      start_time:
        content.start_time &&
        new Date(content.start_time).getTime() > Date.now()
          ? content.start_time
          : new Date(Date.now() + 48 * 60 * 60 * 1000).toISOString(),
      thankyou_message: content.thankyou_message || "",
      signature: content.signature || "",
    };
  }

  if (
    normalizedType === "buskingcard" ||
    normalizedType === "busking_card" ||
    pageType === "BuskingCard" ||
    pageType === "Busking_Card"
  ) {
    return {
      category: content.category || "Singing",
      title: typeof content.title === "string" ? content.title : "",
      base_amount: content.base_amount ?? "",
      start_time: content.start_time || "",
    };
  }

  if (
    normalizedType === "taascard" ||
    normalizedType === "taas_card" ||
    normalizedType === "diamond_card" ||
    normalizedType === "diamondcard" ||
    normalizedType === "clover_card" ||
    normalizedType === "clovercard" ||
    pageType === "TaasCard" ||
    pageType === "Taas_Card" ||
    pageType === "DiamondCard" ||
    pageType === "Diamond_Card" ||
    pageType === "CloverCard" ||
    pageType === "Clover_Card"
  ) {
    return {
      ...content,
      title: typeof content.title === "string" ? content.title : "",
    };
  }

  if (
    normalizedType === "giftcard" ||
    normalizedType === "gift_card" ||
    pageType === "GiftCard" ||
    pageType === "Gift_Card"
  ) {
    return {
      thumbnail: content.thumbnail || "",
      title: content.title || "",
      description: content.description || "",
      price: content.price ?? "",
    };
  }

  if (
    normalizedType === "snapflyer" ||
    normalizedType === "snap_flyer" ||
    pageType === "SnapFlyer" ||
    pageType === "Snap_Flyer"
  ) {
    return {
      description: content.description || "",
      price: content.price !== undefined ? content.price : "",
    };
  }

  if (
    normalizedType === "dearcard" ||
    normalizedType === "dear_card" ||
    pageType === "DearCard" ||
    pageType === "Dear_Card"
  ) {
    return {
      description: content.description || "",
      price: content.price !== undefined ? content.price : "",
    };
  }

  if (
    normalizedType === "aitcard" ||
    normalizedType === "ait_card" ||
    pageType === "AitCard" ||
    pageType === "Ait_Card"
  ) {
    return {
      title: typeof content.title === "string" ? content.title : "",
      description: content.description || "",
      price: content.price !== undefined ? content.price : "",
      seal_time: content.seal_time || "",
      thankyou_message: content.thankyou_message || "",
      signature_url: content.signature_url || "",
      thankyou_image: content.thankyou_image || "",
    };
  }

  if (
    normalizedType === "song4ucard" ||
    normalizedType === "song4u_card" ||
    pageType === "Song4UCard" ||
    pageType === "Song4U_Card"
  ) {
    return {
      title: typeof content.title === "string" ? content.title : "",
      description: content.description || "",
      price: content.price !== undefined ? content.price : "",
    };
  }

  if (
    normalizedType === "bmcard" ||
    normalizedType === "bm_card" ||
    pageType === "BMCard" ||
    pageType === "BM_Card"
  ) {
    const cardSealTime =
      (card as any)?.seal_time || (card as any)?.sealTime || "";
    return {
      title: typeof content.title === "string" ? content.title : "",
      description: content.description || "",
      price: content.price !== undefined ? content.price : "",
      seal_time: content.seal_time || cardSealTime,
      thankyou_message: content.thankyou_message || "",
      signature_url: normalizeMaybeUrl(
        content.signature_url || content.signature || content.signatureUrl
      ),
      thankyou_image: normalizeMaybeUrl(
        content.thankyou_image || content.thankyouImage
      ),
    };
  }

  if (
    normalizedType === "song" ||
    normalizedType === "song4usongs" ||
    pageType === "Song" ||
    pageType === "Song4U_Songs"
  ) {
    const rawSongs = content.songs;
    const songs = Array.isArray(rawSongs)
      ? rawSongs.slice(0, 15).map((song: any) => ({
          name: song?.name ?? "",
          price:
            song?.price === "" ||
            song?.price === undefined ||
            song?.price === null
              ? ""
              : Number(song?.price),
        }))
      : [];

    return {
      title:
        content.title ||
        (t("song4you_songs", { ns: "taascard" }) as string) ||
        "song4you songs",
      songs,
    };
  }

  const cardSealTime =
    typeof (card as PublishedCard)?.seal_time === "string"
      ? (card as PublishedCard).seal_time
      : "";

  const mapSealCardExperienceFields = () => ({
    title: typeof content.title === "string" ? content.title : "",
    description: content.description || "",
    price: content.price !== undefined ? content.price : "",
    seal_time:
      typeof content.seal_time === "string" && content.seal_time.trim()
        ? content.seal_time
        : cardSealTime,
  });

  if (normalizedType === "singlecard" || pageType === "SingleCard") {
    return mapSealCardExperienceFields();
  }

  if (
    normalizedType === "kindiecard" ||
    pageType === "KIndieCard" ||
    pageType === "K_Indie_Card"
  ) {
    return mapSealCardExperienceFields();
  }

  const mapBasicCardExperienceFields = () => ({
    title: typeof content.title === "string" ? content.title : "",
    description: content.description || "",
    price: content.price !== undefined ? content.price : "",
  });

  if (
    normalizedType === "democard" ||
    pageType === "DemoCard" ||
    pageType === "Demo_Card"
  ) {
    return mapBasicCardExperienceFields();
  }

  if (
    normalizedType === "listeningtogethercard" ||
    normalizedType === "listentogethercard" ||
    pageType === "ListeningTogetherCard" ||
    pageType === "Listening_Together_Card" ||
    pageType === "Listen_Together_Card"
  ) {
    return mapBasicCardExperienceFields();
  }

  if (
    normalizedType === "postcard" ||
    normalizedType === "photocard" ||
    pageType === "PostCard" ||
    pageType === "Post_Card" ||
    pageType === "Photocard" ||
    pageType === "PhotoCard"
  ) {
    return {
      title: content.title || "",
      description: content.description || "",
      url: content.url || "",
      signature: content.signature || "",
    };
  }

  if (normalizedType === "youtube" || pageType === "Youtube") {
    return {
      title: content.title || "",
      description: content.description || "",
      url: content.url || "",
    };
  }

  if (normalizedType === "news" || pageType === "News") {
    return normalizeNewsContent(content);
  }

  if (
    normalizedType === "fanclubcard" ||
    normalizedType === "fan_club_card" ||
    pageType === "FanClubCard" ||
    pageType === "Fan_Club_Card"
  ) {
    return {
      talent_name: content.talent_name || "",
      title: typeof content.title === "string" ? content.title : "",
      subtitle: content.subtitle || "",
      description: content.description || "",
      price: content.price !== undefined ? content.price : "",
      edition_year:
        content.edition_year !== undefined
          ? content.edition_year
          : new Date().getFullYear(),
      start_time:
        content.start_time &&
        new Date(content.start_time).getTime() > Date.now()
          ? content.start_time
          : new Date(Date.now() + 48 * 60 * 60 * 1000).toISOString(),
      thankyou_message: content.thankyou_message || "",
      signature: content.signature || "",
      last_page_cover: content.last_page_cover || "",
    };
  }

  if (
    normalizedType === "welcome" ||
    normalizedType === "fanclubwelcome" ||
    pageType === "Welcome" ||
    pageType === "FanClubWelcome" ||
    pageType === "Fan_Club_Welcome"
  ) {
    const mediaUrl =
      content.media_url ||
      content.url ||
      content.video_url ||
      content.audio_url ||
      "";
    const inferFromUrl = (url: string): "video" | "audio" | "" => {
      if (!url) return "";
      const lower = url.toLowerCase();
      if (/\.(mp4|mov|avi|webm|mkv)(\?|$)/.test(lower)) return "video";
      if (/\.(mp3|wav|ogg|aac|flac)(\?|$)/.test(lower)) return "audio";
      return "";
    };

    return {
      title: content.title || "",
      content: content.content || content.description || "",
      media_type:
        (content.media_type as "video" | "audio" | "") ||
        inferFromUrl(mediaUrl),
      media_url: mediaUrl,
    };
  }

  return content;
};
